'use client';

import { FormEvent, useMemo, useState, useEffect, useRef, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { registrationApi, authApi, organizerGovernanceApi, persistAuthToken, getApiErrorMessage, getApiValidationErrorLines } from '@/lib/api';
import { clientEmailVerificationBlock } from '@/lib/email-verification';
import { interpretOtpSendResponse } from '@/lib/otp-delivery';
import { useAuthStore } from '@/lib/auth-store';
import { validateOrgLogo, validateVerificationDocument } from '@/lib/upload-validation';
import {
  dialCodeForCountry,
  isStrongPassword,
  isValidPhoneDigits,
  passwordStrength,
  sanitizePhoneDigits,
} from '@/lib/registration-validation';
import { Mail, CheckCircle2, ArrowRight, ArrowLeft, Eye, EyeOff, Building2, Search, LogIn, Info, MapPin, BadgeCheck, Send, Loader2, FileText, X, Upload } from 'lucide-react';
import { resolveStorageUrl } from '@/lib/storage-url';
import { LocationSelector } from '@/components/ui/LocationSelector';

type OrgType = 'College' | 'University' | 'School' | 'Corporate' | 'NGO' | 'Government' | 'Other';

const orgTypeOptions: OrgType[] = ['College', 'University', 'School', 'Corporate', 'NGO', 'Government', 'Other'];

function RegistrationStepIndicator({ step, totalSteps }: { step: number; totalSteps: number }) {
  return (
    <div className="flex items-center justify-between mb-12 relative px-2">
      <div className="absolute top-5 left-0 w-full h-0.5 bg-nature-muted/10 -translate-y-1/2 z-0" />
      <div
        className="absolute top-5 left-0 h-0.5 bg-landing-primary transition-all duration-700 -translate-y-1/2 z-0"
        style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
      />
      {[1, 2, 3, 4, 5].map((s) => (
        <div key={s} className="relative z-10 flex flex-col items-center">
          <div
            className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-sm transition-all duration-500 ${
              step >= s
                ? 'bg-landing-primary text-text-inverse shadow-[0_8px_20px_-4px_rgba(152,170,157,0.4)]'
                : 'bg-bg border-2 border-nature-muted/20 text-nature-muted'
            } ${step === s ? 'scale-110 ring-4 ring-landing-primary/10' : ''}`}
          >
            {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
          </div>
          <span className={`mt-3 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${step >= s ? 'text-landing-primary' : 'text-nature-muted'}`}>
            {s === 1 ? 'Verify' : s === 2 ? 'Identity' : s === 3 ? 'Location' : s === 4 ? 'Status' : 'Admin'}
          </span>
        </div>
      ))}
    </div>
  );
}

function RegistrationPhoneField({
  label,
  value,
  dialCode,
  onChange,
  required,
}: {
  label: string;
  value: string;
  dialCode: string;
  onChange: (digits: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="flex gap-2">
        <div className="w-20 shrink-0 rounded-lg bg-[#f3f4f6] px-3 py-2.5 text-sm text-[#111827]">{dialCode}</div>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          maxLength={10}
          placeholder="9876543210"
          value={value}
          onChange={(e) => onChange(sanitizePhoneDigits(e.target.value))}
          className="flex-1 rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10 focus:bg-white"
          required={required}
        />
      </div>
      {value && !isValidPhoneDigits(value) && (
        <p className="mt-1.5 text-xs font-medium text-red-600">Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.</p>
      )}
    </div>
  );
}

function OrganizationRegistrationContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAuthenticated, _hasHydrated, authChecking } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>(() => {
    const paramMode = searchParams?.get('mode')?.toLowerCase();
    if (paramMode === 'join') return 'join';
    if (paramMode === 'create') return 'create';
    return 'choose';
  });

  useEffect(() => {
    const paramMode = searchParams?.get('mode')?.toLowerCase();
    if (paramMode === 'join') setMode('join');
    else if (paramMode === 'create') setMode('create');
  }, [searchParams]);
  const [submittedApplicationId, setSubmittedApplicationId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const verifyingOrgOtpRef = useRef(false);
  const verifyingAdminOtpRef = useRef(false);
  const [form, setForm] = useState({
    name: '',
    type: 'College' as OrgType,
    organization_email: '',
    email_verified: false,
    otp: '',
    phone_number: '',
    country_code: '+91',
    website: '',
    year_established: '',
    country: 'India',
    state: '',
    city: '',
    address: '',
    postal_code: '',
    registration_number: '',
    contact_person: '',
    admin_full_name: '',
    admin_email: '',
    admin_email_verified: false,
    admin_otp: '',
    admin_phone: '',
    admin_phone_country_code: '+91',
    admin_designation: '',
    admin_password: '',
    admin_password_confirmation: '',
    description: '',
    social_links_facebook: '',
    social_links_linkedin: '',
    social_links_instagram: '',
  });

  const [files, setFiles] = useState<{
    verification_document: File | null;
    logo: File | null;
  }>({ verification_document: null, logo: null });

  const [step, setStep] = useState(1);
  const [otpLoading, setOtpLoading] = useState(false);
  const [adminOtpLoading, setAdminOtpLoading] = useState(false);
  const [orgOtpSent, setOrgOtpSent] = useState(false);
  const [adminOtpSent, setAdminOtpSent] = useState(false);
  const [joinAccountOtpSent, setJoinAccountOtpSent] = useState(false);
  const [otpRetryable, setOtpRetryable] = useState(false);
  const [resendCooldownEndsAt, setResendCooldownEndsAt] = useState<number | null>(null);
  const [adminResendCooldownEndsAt, setAdminResendCooldownEndsAt] = useState<number | null>(null);
  const [cooldownTick, setCooldownTick] = useState(0);
  const totalSteps = 5;

  // Dynamic join organization search state
  const [joinSearchQuery, setJoinSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('All');
  const [joinOrgs, setJoinOrgs] = useState<any[]>([]);
  const [joinOrgsLoading, setJoinOrgsLoading] = useState(false);
  const [joinOrgsError, setJoinOrgsError] = useState<string | null>(null);
  const [selectedJoinOrg, setSelectedJoinOrg] = useState<any | null>(null);
  const [joinOrgRoles, setJoinOrgRoles] = useState<any[]>([]);
  const [joinForm, setJoinForm] = useState({
    designation: '',
    department: '',
    role_id: '',
    notes: '',
  });
  const [joinSubmittedOrg, setJoinSubmittedOrg] = useState<any | null>(null);
  const [submittingJoin, setSubmittingJoin] = useState(false);

  // Join account creation state for unauthenticated users
  const [joinAccountForm, setJoinAccountForm] = useState({
    email: '',
    email_verified: false,
    otp: '',
    name: '',
    password: '',
    password_confirmation: '',
    designation: '',
    department: '',
    role_id: '',
    notes: '',
  });
  const [joinAccountOtpLoading, setJoinAccountOtpLoading] = useState(false);
  const [joinAccountCooldownEndsAt, setJoinAccountCooldownEndsAt] = useState<number | null>(null);
  const verifyingJoinAccountOtpRef = useRef(false);
  const [showJoinAccountPassword, setShowJoinAccountPassword] = useState(false);
  const [showJoinAccountConfirmPassword, setShowJoinAccountConfirmPassword] = useState(false);
  const [submittingJoinAccount, setSubmittingJoinAccount] = useState(false);
  const [joinAccountError, setJoinAccountError] = useState<{ message: string; isExpired?: boolean } | null>(null);

  const adminEmailNeedsVerification = useMemo(() => {
    const org = form.organization_email.trim().toLowerCase();
    const admin = form.admin_email.trim().toLowerCase();
    return org !== '' && admin !== '' && org !== admin;
  }, [form.organization_email, form.admin_email]);

  const otpTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (otpTimerRef.current != null) {
      window.clearInterval(otpTimerRef.current);
      otpTimerRef.current = null;
    }
    const active =
      (resendCooldownEndsAt && Date.now() < resendCooldownEndsAt) ||
      (adminResendCooldownEndsAt && Date.now() < adminResendCooldownEndsAt) ||
      (joinAccountCooldownEndsAt && Date.now() < joinAccountCooldownEndsAt);
    if (!active) return;
    otpTimerRef.current = window.setInterval(() => setCooldownTick((t) => t + 1), 1000);
    return () => {
      if (otpTimerRef.current != null) {
        window.clearInterval(otpTimerRef.current);
        otpTimerRef.current = null;
      }
    };
  }, [resendCooldownEndsAt, adminResendCooldownEndsAt, joinAccountCooldownEndsAt]);

  const otpResendWaitSeconds = useMemo(() => {
    if (!resendCooldownEndsAt) return 0;
    return Math.max(0, Math.ceil((resendCooldownEndsAt - Date.now()) / 1000));
  }, [resendCooldownEndsAt, cooldownTick]);

  const adminOtpResendWaitSeconds = useMemo(() => {
    if (!adminResendCooldownEndsAt) return 0;
    return Math.max(0, Math.ceil((adminResendCooldownEndsAt - Date.now()) / 1000));
  }, [adminResendCooldownEndsAt, cooldownTick]);

  const joinAccountOtpResendWaitSeconds = useMemo(() => {
    if (!joinAccountCooldownEndsAt) return 0;
    return Math.max(0, Math.ceil((joinAccountCooldownEndsAt - Date.now()) / 1000));
  }, [joinAccountCooldownEndsAt, cooldownTick]);

  const docInputRef = useRef<HTMLInputElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const verificationDocPreviewUrl = useMemo(() => {
    if (!files.verification_document) return null;
    const isImage =
      files.verification_document.type.startsWith('image/') ||
      /\.(jpe?g|png|webp)$/i.test(files.verification_document.name);
    return isImage ? URL.createObjectURL(files.verification_document) : null;
  }, [files.verification_document]);

  useEffect(() => {
    return () => {
      if (verificationDocPreviewUrl) URL.revokeObjectURL(verificationDocPreviewUrl);
    };
  }, [verificationDocPreviewUrl]);

  const logoPreviewUrl = useMemo(
    () =>
      files.logo &&
      (files.logo.type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(files.logo.name))
        ? URL.createObjectURL(files.logo)
        : null,
    [files.logo],
  );

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  useEffect(() => {
    if (!adminEmailNeedsVerification && form.admin_email.trim()) {
      update('admin_email_verified', true);
    } else if (adminEmailNeedsVerification) {
      update('admin_email_verified', false);
    }
  }, [adminEmailNeedsVerification, form.admin_email]);

  useEffect(() => {
    const code = dialCodeForCountry(form.country);
    setForm((prev) => ({
      ...prev,
      country_code: code,
      admin_phone_country_code: code,
    }));
  }, [form.country]);

  const pwdStrength = useMemo(() => passwordStrength(form.admin_password), [form.admin_password]);

  const canSubmit = useMemo(() => {
    return (
      form.email_verified &&
      form.name.trim() &&
      form.type &&
      form.organization_email.trim() &&
      isValidPhoneDigits(form.phone_number) &&
      form.website.trim() &&
      form.year_established.trim() &&
      form.country.trim() &&
      form.state.trim() &&
      form.city.trim() &&
      form.address.trim() &&
      form.postal_code.trim() &&
      form.registration_number.trim() &&
      form.contact_person.trim() &&
      files.verification_document &&
      files.logo &&
      form.admin_full_name.trim() &&
      form.admin_email.trim() &&
      (!adminEmailNeedsVerification || form.admin_email_verified) &&
      isValidPhoneDigits(form.admin_phone) &&
      form.admin_designation.trim() &&
      isStrongPassword(form.admin_password) &&
      form.admin_password === form.admin_password_confirmation
    );
  }, [form, files, adminEmailNeedsVerification]);

  const update = (key: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (mode !== 'join') return;
    setJoinOrgsLoading(true);
    setJoinOrgsError(null);
    const timer = setTimeout(async () => {
      try {
        const params: Record<string, unknown> = {
          q: joinSearchQuery.trim(),
          per_page: 30,
        };
        if (selectedCategoryFilter && selectedCategoryFilter !== 'All') {
          params.type = selectedCategoryFilter;
        }
        const res = await organizerGovernanceApi.searchOrganizations(params);
        const list = res.data?.data?.data || res.data?.data || [];
        setJoinOrgs(list);
        setJoinOrgsError(null);
      } catch (err: any) {
        console.error('Failed to search organizations:', err);
        const status = err?.response?.status;
        const msg =
          status === 401
            ? 'Authentication required or session expired. Please sign in to view and join organizations.'
            : err?.response?.data?.message || 'Failed to load organizations. Please try again later.';
        toast.error(msg);
        setJoinOrgsError(msg);
        setJoinOrgs([]);
      } finally {
        setJoinOrgsLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [mode, joinSearchQuery, selectedCategoryFilter]);

  useEffect(() => {
    if (!selectedJoinOrg) {
      setJoinOrgRoles([]);
      return;
    }
    organizerGovernanceApi
      .organizationRoles(selectedJoinOrg.id)
      .then((res) => setJoinOrgRoles(res.data?.data || []))
      .catch(() => setJoinOrgRoles([]));
  }, [selectedJoinOrg]);

  const handleSubmitJoinRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedJoinOrg) return;
    if (!joinForm.designation.trim()) {
      toast.error('Designation is required.');
      return;
    }
    setSubmittingJoin(true);
    try {
      const meRes = await authApi.me();
      const meUser = meRes.data?.data ?? meRes.data?.user ?? (meRes.data?.id ? meRes.data : null) ?? useAuthStore.getState().user;
      if (!meUser?.id) {
        toast.error('Session expired or authentication required. Redirecting to sign in...');
        router.push(`/login?redirect=/register/organization/`);
        return;
      }

      const verificationBlock = clientEmailVerificationBlock(meUser);
      if (verificationBlock === 'unverified') {
        toast.error('Please verify your email address to submit a join request.');
        return;
      }

      await organizerGovernanceApi.submitRequest({
        organizer_type: 'organization',
        organization_id: selectedJoinOrg.id,
        organization_role_id: joinForm.role_id || undefined,
        designation: joinForm.designation.trim(),
        department: joinForm.department.trim() || undefined,
        notes: joinForm.notes.trim() || undefined,
      });
      setJoinSubmittedOrg(selectedJoinOrg);
      toast.success(`Request sent to ${selectedJoinOrg.name}!`);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message || getApiErrorMessage(err, 'Failed to submit join request.');

      if (code === 'AUTH_REQUIRED' || err?.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        router.push(`/login?redirect=/register/organization/`);
      } else if (code === 'EMAIL_NOT_VERIFIED') {
        toast.error('Email verification required to submit a join request.');
      } else if (code === 'REQUEST_ALREADY_PENDING' || (err?.response?.status === 409 && message.toLowerCase().includes('pending'))) {
        toast.error('A pending join request already exists for this organization.');
      } else if (code === 'ALREADY_MEMBER' || (err?.response?.status === 409 && message.toLowerCase().includes('already'))) {
        toast.error('You are already an active organizer or member of this organization.');
      } else {
        toast.error(message);
      }
    } finally {
      setSubmittingJoin(false);
    }
  };

  const joinAccountPwdStrength = useMemo(
    () => passwordStrength(joinAccountForm.password),
    [joinAccountForm.password]
  );

  const handleSendJoinAccountOtp = async () => {
    const email = joinAccountForm.email.trim();
    if (!email || joinAccountOtpLoading) {
      if (!email) toast.error('Please enter an organizer email address.');
      return;
    }
    if (joinAccountOtpResendWaitSeconds > 0) return;

    setJoinAccountOtpLoading(true);
    setJoinAccountError(null);
    setOtpRetryable(false);
    try {
      const res = await authApi.sendOtp(email);
      const outcome = interpretOtpSendResponse(res.status, res.data);
      if (!outcome.ok) {
        setOtpRetryable(outcome.retryable);
        setJoinAccountOtpSent(false);
        toast.error(outcome.message);
        return;
      }
      const cd = outcome.cooldownSeconds ?? 60;
      setJoinAccountCooldownEndsAt(Date.now() + cd * 1000);
      setJoinAccountOtpSent(true);
      if (outcome.devOtp) {
        toast.success(`Verification code sent! (Dev code: ${outcome.devOtp})`, { duration: 12000 });
      } else {
        toast.success('Verification code sent to your email.');
      }
    } catch (err: any) {
      const outcome = interpretOtpSendResponse(err?.response?.status ?? 0, err?.response?.data);
      setOtpRetryable(outcome.retryable || (err?.response?.status ?? 0) >= 500);
      setJoinAccountOtpSent(false);
      toast.error(outcome.message && outcome.message !== 'Failed to send verification code.'
        ? outcome.message
        : getApiErrorMessage(err, 'Failed to send verification code.'));
    } finally {
      setJoinAccountOtpLoading(false);
    }
  };

  const handleResendJoinAccountOtp = async () => {
    const email = joinAccountForm.email.trim();
    if (!email) {
      toast.error('Please enter an organizer email address.');
      return;
    }
    if (joinAccountOtpResendWaitSeconds > 0 || joinAccountOtpLoading) return;

    setJoinAccountOtpLoading(true);
    try {
      const res = await authApi.sendOtp(email);
      const outcome = interpretOtpSendResponse(res.status, res.data);
      if (!outcome.ok) {
        setOtpRetryable(outcome.retryable);
        toast.error(outcome.message);
        return;
      }
      const cd = outcome.cooldownSeconds ?? 60;
      setJoinAccountCooldownEndsAt(Date.now() + cd * 1000);
      verifyingJoinAccountOtpRef.current = false;
      setJoinAccountForm((prev) => ({ ...prev, email_verified: false, otp: '' }));
      setJoinAccountOtpSent(true);
      setJoinAccountError(null);
      setOtpRetryable(false);
      if (outcome.devOtp) {
        toast.success(`New verification code sent! (Dev code: ${outcome.devOtp})`, { duration: 12000 });
      } else {
        toast.success('New verification code sent to your email.');
      }
    } catch (err: any) {
      const outcome = interpretOtpSendResponse(err?.response?.status ?? 0, err?.response?.data);
      setOtpRetryable(outcome.retryable || (err?.response?.status ?? 0) >= 500);
      toast.error(outcome.message && outcome.message !== 'Failed to send verification code.'
        ? outcome.message
        : getApiErrorMessage(err, 'Failed to send new verification code.'));
    } finally {
      setJoinAccountOtpLoading(false);
    }
  };

  const handleVerifyJoinAccountOtp = async () => {
    if (verifyingJoinAccountOtpRef.current || joinAccountOtpLoading || joinAccountForm.email_verified) return;
    const email = joinAccountForm.email.trim();
    const code = joinAccountForm.otp.trim();
    if (!code) {
      toast.error('Please enter the 6-digit verification code.');
      return;
    }
    if (code.length !== 6) {
      toast.error('Verification code must be 6 digits.');
      return;
    }

    verifyingJoinAccountOtpRef.current = true;
    setJoinAccountOtpLoading(true);
    setJoinAccountError(null);
    try {
      await authApi.verifyOtp(email, code);
      setJoinAccountForm((prev) => ({ ...prev, email_verified: true }));
      setJoinAccountError(null);
      toast.success('Organizer email verified successfully!');
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Couldn't verify code. Please check and try again.";
      toast.error(msg);
      verifyingJoinAccountOtpRef.current = false;
    } finally {
      setJoinAccountOtpLoading(false);
    }
  };

  const handleCreateAccountAndJoin = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedJoinOrg) return;

    if (!joinAccountForm.email_verified) {
      toast.error('Please verify your organizer email address first.');
      return;
    }
    if (!joinAccountForm.name.trim()) {
      toast.error('Full name is required.');
      return;
    }
    if (!isStrongPassword(joinAccountForm.password)) {
      toast.error('Password must meet strength requirements (min 8 chars, uppercase, lowercase, number/symbol).');
      return;
    }
    if (joinAccountForm.password !== joinAccountForm.password_confirmation) {
      toast.error('Passwords do not match.');
      return;
    }
    if (!joinAccountForm.designation.trim()) {
      toast.error('Designation / Role Title is required.');
      return;
    }

    setSubmittingJoinAccount(true);
    setJoinAccountError(null);
    try {
      // 1. Create Organizer Account & log in
      const regRes = await authApi.registerWithOtp(
        joinAccountForm.email.trim(),
        joinAccountForm.name.trim(),
        joinAccountForm.password,
        joinAccountForm.otp.trim(),
        'organizer'
      );

      // Hydrate client auth state with the created account session
      if (regRes?.data) {
        persistAuthToken(regRes.data);
        const regUser = regRes.data.user ?? regRes.data.data?.user;
        if (regUser) {
          useAuthStore.getState().setUser(regUser);
          useAuthStore.setState({ isAuthenticated: true, authChecking: false });
        }
      }

      // Refresh client auth store state
      await useAuthStore.getState().checkAuth();

      // Ensure the identity is fully settled server-side before submitting
      const meRes = await authApi.me();
      const meUser = meRes.data?.data ?? meRes.data?.user ?? (meRes.data?.id ? meRes.data : null) ?? useAuthStore.getState().user;
      if (!meUser?.id) throw new Error('Account created but authentication session could not be established. Please sign in.');

      // 2. Submit join request to selected organization
      await organizerGovernanceApi.submitRequest({
        organizer_type: 'organization',
        organization_id: selectedJoinOrg.id,
        organization_role_id: joinAccountForm.role_id || undefined,
        designation: joinAccountForm.designation.trim(),
        department: joinAccountForm.department.trim() || undefined,
        notes: joinAccountForm.notes.trim() || undefined,
      });

      setJoinForm({
        designation: joinAccountForm.designation.trim(),
        department: joinAccountForm.department.trim(),
        role_id: joinAccountForm.role_id,
        notes: joinAccountForm.notes.trim(),
      });
      setJoinSubmittedOrg(selectedJoinOrg);
      toast.success('Organizer account created & Join request submitted successfully!');
    } catch (err: any) {
      const errCode = err?.response?.data?.error_code;
      const status = err?.response?.status;
      const errMsg = status === 401
        ? 'Session expired. Please sign in again.'
        : getApiErrorMessage(err, 'Failed to create account or submit join request.');
      const isExpired = errCode === 'otp_expired' || errCode === 'otp_not_found';

      setJoinAccountError({
        message: errMsg,
        isExpired,
      });

      if (isExpired) {
        verifyingJoinAccountOtpRef.current = false;
        setJoinAccountForm((prev) => ({ ...prev, email_verified: false }));
      }

      toast.error(errMsg);
    } finally {
      setSubmittingJoinAccount(false);
    }
  };

  const filteredJoinOrgs = useMemo(() => {
    if (selectedCategoryFilter === 'All') return joinOrgs;
    return joinOrgs.filter((org) => (org.type || org.category || '').toLowerCase() === selectedCategoryFilter.toLowerCase());
  }, [joinOrgs, selectedCategoryFilter]);

  const validateStep = (currentStep: number): boolean => {
    if (currentStep === 2) {
      if (!form.name.trim()) {
        toast.error('Organization name is required.');
        return false;
      }
      if (!isValidPhoneDigits(form.phone_number)) {
        toast.error('Enter a valid 10-digit phone number.');
        return false;
      }
      if (!form.website.trim()) {
        toast.error('Website is required.');
        return false;
      }
      if (!form.year_established.trim()) {
        toast.error('Year Established is required.');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!form.country.trim() || !form.state.trim() || !form.city.trim() || !form.address.trim()) {
        toast.error('Complete all required location fields.');
        return false;
      }
      if (!form.postal_code.trim()) {
        toast.error('Postal Code is required.');
        return false;
      }
    }
    if (currentStep === 4) {
      if (!form.registration_number.trim()) {
        toast.error('Registration Number is required.');
        return false;
      }
      if (!form.contact_person.trim()) {
        toast.error('Primary contact person is required.');
        return false;
      }
      const docErr = validateVerificationDocument(files.verification_document, true);
      if (docErr) {
        toast.error(docErr);
        return false;
      }
      const logoErr = validateOrgLogo(files.logo, true);
      if (logoErr) {
        toast.error(logoErr);
        return false;
      }
    }
    if (currentStep === 5) {
      if (!form.admin_full_name.trim() || !form.admin_email.trim() || !form.admin_designation.trim()) {
        toast.error('Complete all required admin fields.');
        return false;
      }
      if (!isValidPhoneDigits(form.admin_phone)) {
        toast.error('Enter a valid 10-digit admin phone.');
        return false;
      }
      if (adminEmailNeedsVerification && !form.admin_email_verified) {
        toast.error('Verify admin email with OTP.');
        return false;
      }
      if (!isStrongPassword(form.admin_password)) {
        toast.error('Password must meet all strength requirements.');
        return false;
      }
      if (form.admin_password !== form.admin_password_confirmation) {
        toast.error('Passwords do not match.');
        return false;
      }
    }
    return true;
  };

  const handleContinue = () => {
    if (!validateStep(step)) return;
    setStep((s) => s + 1);
  };

  const handleSendOtp = async (target: 'organization' | 'admin') => {
    const email = target === 'organization' ? form.organization_email : form.admin_email;
    const alreadySending = target === 'organization' ? otpLoading : adminOtpLoading;
    if (!email.trim()) {
      toast.error('Please enter an email address.');
      return;
    }
    if (alreadySending) return;
    const wait = target === 'organization' ? otpResendWaitSeconds : adminOtpResendWaitSeconds;
    if (wait > 0) return;

    const setBusy = target === 'organization' ? setOtpLoading : setAdminOtpLoading;
    const setSent = target === 'organization' ? setOrgOtpSent : setAdminOtpSent;
    setBusy(true);
    setOtpError(null);
    setOtpRetryable(false);
    try {
      const res = await authApi.sendOtp(email);
      const outcome = interpretOtpSendResponse(res.status, res.data);
      if (!outcome.ok) {
        setSent(false);
        setOtpRetryable(outcome.retryable);
        setOtpError(outcome.message);
        toast.error(outcome.message);
        return;
      }
      const cd = outcome.cooldownSeconds ?? 60;
      if (target === 'organization') {
        setResendCooldownEndsAt(Date.now() + cd * 1000);
      } else {
        setAdminResendCooldownEndsAt(Date.now() + cd * 1000);
      }
      setSent(true);
      if (outcome.devOtp) {
        toast.success(`Verification code sent! (Dev code: ${outcome.devOtp})`, { duration: 12000 });
      } else {
        toast.success('Verification code sent to your email.');
      }
    } catch (err: any) {
      const outcome = interpretOtpSendResponse(err?.response?.status ?? 0, err?.response?.data);
      const ra = err?.response?.data?.retry_after ?? outcome.cooldownSeconds;
      if (typeof ra === 'number' && ra > 0) {
        if (target === 'organization') setResendCooldownEndsAt(Date.now() + ra * 1000);
        else setAdminResendCooldownEndsAt(Date.now() + ra * 1000);
      }
      const message = outcome.message && outcome.message !== 'Failed to send verification code.'
        ? outcome.message
        : getApiErrorMessage(err, 'Failed to send OTP.');
      setSent(false);
      setOtpRetryable(outcome.retryable || (err?.response?.status ?? 0) >= 500);
      setOtpError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (target: 'organization' | 'admin') => {
    const isAlreadyVerified = target === 'organization' ? form.email_verified : form.admin_email_verified;
    const isVerifyingRef = target === 'organization' ? verifyingOrgOtpRef : verifyingAdminOtpRef;
    const isLoading = target === 'organization' ? otpLoading : adminOtpLoading;
    if (isVerifyingRef.current || isLoading || isAlreadyVerified) return;

    const email = target === 'organization' ? form.organization_email : form.admin_email;
    const code = (target === 'organization' ? form.otp : form.admin_otp).trim();
    if (!code) {
      toast.error('Please enter the verification code.');
      return;
    }
    if (code.length !== 6) {
      toast.error('Verification code must be 6 digits.');
      return;
    }

    const setBusy = target === 'organization' ? setOtpLoading : setAdminOtpLoading;
    setOtpError(null);
    isVerifyingRef.current = true;
    setBusy(true);
    try {
      await authApi.verifyOtp(email, code);
      if (target === 'organization') {
        update('email_verified', true);
        toast.success('Organization email verified!');
        setStep(2);
      } else {
        update('admin_email_verified', true);
        toast.success('Admin email verified!');
      }
      // Keep isVerifyingRef.current = true on success so rapid Enter key events cannot re-trigger verification
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.otp?.[0] ||
        "We couldn't verify your OTP. Please check the code and try again.";
      setOtpError(msg);
      toast.error(msg);
      isVerifyingRef.current = false;
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      if (!form.email_verified && !verifyingOrgOtpRef.current && !otpLoading && form.otp.trim().length === 6) {
        handleVerifyOtp('organization');
      }
      return;
    }
    if (step < 5) {
      handleContinue();
      return;
    }
    if (!canSubmit || loading) return;

    setLoading(true);
    try {
      const payload = new FormData();
      payload.set('name', form.name);
      payload.set('type', form.type);
      payload.set('organization_email', form.organization_email);
      payload.set('phone_number', form.phone_number);
      payload.set('country_code', form.country_code);
      payload.set('website', form.website);
      payload.set('year_established', form.year_established);
      payload.set('country', form.country);
      payload.set('state', form.state);
      payload.set('city', form.city);
      payload.set('address', form.address);
      payload.set('postal_code', form.postal_code);
      payload.set('registration_number', form.registration_number);
      payload.set('contact_person', form.contact_person);
      payload.set('admin_full_name', form.admin_full_name);
      payload.set('admin_email', form.admin_email);
      payload.set('admin_phone', form.admin_phone);
      payload.set('admin_phone_country_code', form.admin_phone_country_code);
      payload.set('admin_designation', form.admin_designation);
      payload.set('admin_password', form.admin_password);
      payload.set('admin_password_confirmation', form.admin_password_confirmation);
      if (form.description) payload.set('description', form.description);

      const socialLinks: Record<string, string> = {};
      if (form.social_links_facebook) socialLinks.facebook = form.social_links_facebook;
      if (form.social_links_linkedin) socialLinks.linkedin = form.social_links_linkedin;
      if (form.social_links_instagram) socialLinks.instagram = form.social_links_instagram;
      if (Object.keys(socialLinks).length > 0) {
        payload.set('social_links', JSON.stringify(socialLinks));
      }

      payload.append('verification_document', files.verification_document!);
      payload.append('logo', files.logo!);

      const res = await registrationApi.registerOrganization(payload);
      const applicationId = res.data?.data?.application_id ?? null;
      setSubmittedApplicationId(applicationId);
      toast.success('Registration submitted successfully.');
    } catch (err: any) {
      const lines = getApiValidationErrorLines(err);
      toast.error(lines.length ? lines.slice(0, 5).join(' · ') : getApiErrorMessage(err, 'Registration failed.'));
    } finally {
      setLoading(false);
    }
  };

  if (submittedApplicationId) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-landing-bg via-white to-emerald-50/30 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <Card variant="solid" className="rounded-[40px] border-none shadow-xl bg-bg/95 p-10 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-nature-text">Registration Successful</h1>
            <p className="mt-4 text-nature-muted">Your Application ID:</p>
            <p className="mt-2 text-2xl font-bold text-landing-primary">{submittedApplicationId}</p>
            <p className="mt-6 text-nature-muted">Review may take up to 24 hours.</p>
            <p className="mt-2 text-sm text-nature-muted">
              For urgent requests:{' '}
              <a href="mailto:enthioshub@gmail.com" className="text-landing-primary font-semibold">
                enthioshub@gmail.com
              </a>
            </p>
            <Link href="/login" className="inline-block mt-8">
              <Button>Go to Sign In</Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  if (mode === 'choose') {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-landing-bg via-white to-emerald-50/30 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-nature-text">Partner with EnthiosHub</h1>
              <p className="text-nature-muted font-medium mt-1">Create a new organization, or join one that already exists.</p>
            </div>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 text-sm font-bold text-landing-primary hover:text-landing-primaryHover transition-colors border-b-2 border-landing-primary/20 hover:border-landing-primary pb-0.5 w-fit"
            >
              ← Back to Selection
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <button
              type="button"
              onClick={() => setMode('create')}
              className="text-left rounded-[32px] bg-bg/95 border border-nature-muted/10 shadow-xl p-8 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-landing-primary/10 flex items-center justify-center mb-5">
                <Building2 className="w-7 h-7 text-landing-primary" />
              </div>
              <h2 className="text-xl font-bold text-nature-text">Create New Organization</h2>
              <p className="text-nature-muted mt-2 text-sm">Register your institution, company, or NGO and set up its admin account. Submitted for approval.</p>
              <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-landing-primary">
                Get started <ArrowRight className="w-4 h-4" />
              </span>
            </button>

            <button
              type="button"
              onClick={() => setMode('join')}
              className="text-left rounded-[32px] bg-bg/95 border border-nature-muted/10 shadow-xl p-8 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-5">
                <Search className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-nature-text">Join Existing Organization</h2>
              <p className="text-nature-muted mt-2 text-sm">Search for a verified organization and request to join with a role. Access is granted once an admin approves.</p>
              <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-emerald-600">
                Search organizations <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'join') {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-landing-bg via-white to-emerald-50/30 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <button
            type="button"
            onClick={() => {
              if (selectedJoinOrg || joinSubmittedOrg) {
                setSelectedJoinOrg(null);
                setJoinSubmittedOrg(null);
              } else {
                setMode('choose');
              }
            }}
            className="inline-flex items-center gap-2 text-sm font-bold text-landing-primary hover:text-landing-primaryHover transition-colors mb-8"
          >
            ← Back to {selectedJoinOrg || joinSubmittedOrg ? 'Organization Selection' : 'Options'}
          </button>

          {joinSubmittedOrg ? (
            <Card variant="solid" className="rounded-[40px] border-none shadow-xl bg-bg/95 p-10 text-center max-w-2xl mx-auto">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6 text-emerald-600 animate-in zoom-in-95 duration-300">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h1 className="text-3xl font-bold text-nature-text">Request Submitted</h1>
              <p className="text-nature-muted mt-3 text-base leading-relaxed">
                Your request to join <strong className="text-nature-text">{joinSubmittedOrg.name}</strong> as{' '}
                <strong className="text-nature-text">&quot;{joinForm.designation}&quot;</strong> has been sent for review.
              </p>
              <div className="mt-6 p-4 rounded-2xl bg-emerald-50/80 border border-emerald-100 text-left text-xs text-emerald-800 space-y-1">
                <p className="font-semibold text-emerald-900">What happens next?</p>
                <p>• An administrator of {joinSubmittedOrg.name} will review your application in their dashboard.</p>
                <p>• Once approved, your account will be activated under {joinSubmittedOrg.name} and you will receive an email notification.</p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => {
                    setJoinSubmittedOrg(null);
                    setSelectedJoinOrg(null);
                  }}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  ← Browse Other Organizations
                </Button>
                <Link href="/login">
                  <Button className="w-full sm:w-auto inline-flex items-center gap-2">
                    <LogIn className="w-4 h-4" /> Go to Login
                  </Button>
                </Link>
              </div>
            </Card>
          ) : selectedJoinOrg ? (
            <Card variant="solid" className="rounded-[40px] border-none shadow-xl bg-bg/95 p-8 max-w-2xl mx-auto">
              <div className="flex items-center gap-4 border-b border-nature-muted/10 pb-6 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-landing-primary/10 flex items-center justify-center text-landing-primary font-bold text-xl overflow-hidden shrink-0">
                  {resolveStorageUrl(selectedJoinOrg.logo_url) ? (
                    <img src={resolveStorageUrl(selectedJoinOrg.logo_url)!} alt={selectedJoinOrg.name} className="w-full h-full object-cover" />
                  ) : (
                    selectedJoinOrg.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-nature-text">{selectedJoinOrg.name}</h2>
                    <BadgeCheck className="w-5 h-5 text-emerald-600 fill-emerald-50 shrink-0" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-nature-muted">
                    <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 font-bold text-emerald-700 border border-emerald-200">
                      {selectedJoinOrg.type || selectedJoinOrg.category || 'Organization'}
                    </span>
                    {(selectedJoinOrg.city || selectedJoinOrg.country) && (
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <MapPin className="w-3.5 h-3.5" />
                        {[selectedJoinOrg.city, selectedJoinOrg.country].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {isAuthenticated ? (
                <form onSubmit={handleSubmitJoinRequest} className="space-y-5">
                  <h3 className="text-lg font-bold text-nature-text">Request Details</h3>
                  <p className="text-xs text-nature-muted">
                    Provide your role preferences so the organization administrator can verify and assign your permissions.
                  </p>

                  <div>
                    <Input
                      label="Designation / Role Title"
                      placeholder="e.g. Lead Event Coordinator, Student Head, Operations Admin"
                      value={joinForm.designation}
                      onChange={(e) => setJoinForm((prev) => ({ ...prev, designation: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Input
                      label="Department / Faculty (Optional)"
                      placeholder="e.g. Department of Computer Science, Cultural Committee"
                      value={joinForm.department}
                      onChange={(e) => setJoinForm((prev) => ({ ...prev, department: e.target.value }))}
                    />
                  </div>

                  {joinOrgRoles.length > 0 && (
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                        Requested Role Preference
                      </label>
                      <select
                        value={joinForm.role_id}
                        onChange={(e) => setJoinForm((prev) => ({ ...prev, role_id: e.target.value }))}
                        className="w-full rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10"
                      >
                        <option value="">-- Standard Organizer --</option>
                        {joinOrgRoles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.role_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                      Request Notes for Administrator (Optional)
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Briefly describe your responsibilities or why you are joining this organization..."
                      value={joinForm.notes}
                      onChange={(e) => setJoinForm((prev) => ({ ...prev, notes: e.target.value }))}
                      className="w-full rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10"
                    />
                  </div>

                  <div className="pt-4 flex items-center justify-between gap-3 border-t border-nature-muted/10">
                    <Button type="button" variant="outline" onClick={() => setSelectedJoinOrg(null)}>
                      ← Back to List
                    </Button>
                    <Button type="submit" loading={submittingJoin} className="inline-flex items-center gap-2">
                      <Send className="w-4 h-4" /> Submit Join Request
                    </Button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleCreateAccountAndJoin} className="space-y-6">
                  {/* Section 1: Email Verification */}
                  <div className="space-y-4">
                    <h3 className="text-base font-bold text-nature-text flex items-center gap-2">
                      <Mail className="w-4 h-4 text-landing-primary" /> Verify Organizer Email
                    </h3>

                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                        Organizer Email <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          autoComplete="email"
                          placeholder="organizer@example.com"
                          value={joinAccountForm.email}
                          onChange={(e) => {
                            setJoinAccountOtpSent(false);
                            setOtpRetryable(false);
                            setJoinAccountForm((prev) => ({ ...prev, email: e.target.value, email_verified: false, otp: '' }));
                          }}
                          disabled={joinAccountForm.email_verified}
                          className="flex-1 rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10 disabled:opacity-60"
                          required
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleSendJoinAccountOtp}
                          disabled={
                            joinAccountOtpLoading ||
                            joinAccountOtpResendWaitSeconds > 0 ||
                            joinAccountForm.email_verified ||
                            !joinAccountForm.email.trim()
                          }
                          className="shrink-0 text-xs"
                        >
                          {joinAccountOtpLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : joinAccountOtpResendWaitSeconds > 0 ? (
                            `Resend in ${joinAccountOtpResendWaitSeconds}s`
                          ) : joinAccountForm.email_verified ? (
                            'Verified'
                          ) : (
                            'Send Code'
                          )}
                        </Button>
                      </div>
                    </div>

                    {joinAccountOtpSent && !joinAccountForm.email_verified && (
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                          6-Digit Verification Code <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            maxLength={6}
                            placeholder="123456"
                            value={joinAccountForm.otp}
                            onChange={(e) =>
                              setJoinAccountForm((prev) => ({ ...prev, otp: e.target.value.replace(/\D/g, '') }))
                            }
                            className="flex-1 rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 text-sm font-mono tracking-widest text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10"
                          />
                          <Button
                            type="button"
                            onClick={handleVerifyJoinAccountOtp}
                            disabled={joinAccountOtpLoading || joinAccountForm.otp.length !== 6}
                            className="shrink-0 text-xs"
                          >
                            Verify Code
                          </Button>
                        </div>
                      </div>
                    )}

                    {joinAccountForm.email_verified && (
                      <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-emerald-800 text-xs font-semibold">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span>Organizer Email Verified</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleResendJoinAccountOtp}
                          disabled={joinAccountOtpLoading || joinAccountOtpResendWaitSeconds > 0}
                          className="text-xs font-medium text-emerald-700 hover:text-emerald-900 underline underline-offset-2 disabled:opacity-50 disabled:no-underline cursor-pointer disabled:cursor-not-allowed shrink-0 flex items-center gap-1.5"
                        >
                          {joinAccountOtpLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : joinAccountOtpResendWaitSeconds > 0 ? (
                            `Resend available in ${joinAccountOtpResendWaitSeconds}s`
                          ) : (
                            'Resend OTP / Re-verify'
                          )}
                        </button>
                      </div>
                    )}

                    {joinAccountError && (
                      <div className={`p-4 rounded-xl text-sm border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                        joinAccountError.isExpired 
                          ? 'bg-amber-50 border-amber-200 text-amber-900' 
                          : 'bg-red-50 border-red-200 text-red-900'
                      }`}>
                        <div className="flex items-start gap-2.5">
                          <Info className={`w-5 h-5 shrink-0 mt-0.5 ${joinAccountError.isExpired ? 'text-amber-600' : 'text-red-600'}`} />
                          <div>
                            <p className="font-semibold text-xs uppercase tracking-wider mb-0.5">
                              {joinAccountError.isExpired ? 'OTP Expired' : 'Registration Error'}
                            </p>
                            <p className="text-xs leading-relaxed">{joinAccountError.message}</p>
                          </div>
                        </div>
                        {joinAccountError.isExpired && (
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            onClick={handleResendJoinAccountOtp}
                            disabled={joinAccountOtpLoading || joinAccountOtpResendWaitSeconds > 0}
                            className="shrink-0 text-xs self-end sm:self-center"
                          >
                            {joinAccountOtpLoading ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                            ) : null}
                            {joinAccountOtpResendWaitSeconds > 0
                              ? `Resend in ${joinAccountOtpResendWaitSeconds}s`
                              : 'Resend New OTP'}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Section 2: Account Details & Password */}
                  <div className="space-y-4 pt-3 border-t border-nature-muted/10">
                    <h3 className="text-base font-bold text-nature-text">Organizer Account Details</h3>

                    <div>
                      <Input
                        label="Full Name"
                        autoComplete="name"
                        placeholder="John Doe"
                        value={joinAccountForm.name}
                        onChange={(e) => setJoinAccountForm((prev) => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="relative">
                        <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                          Create Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showJoinAccountPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="••••••••"
                            value={joinAccountForm.password}
                            onChange={(e) =>
                              setJoinAccountForm((prev) => ({ ...prev, password: e.target.value }))
                            }
                            className="w-full rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 pr-10 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowJoinAccountPassword(!showJoinAccountPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-nature-muted hover:text-nature-text"
                          >
                            {showJoinAccountPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>

                        {joinAccountForm.password && (
                          <div className="mt-2 space-y-1">
                            <div className="flex gap-1 h-1">
                              {[1, 2, 3, 4].map((bar) => (
                                <div
                                  key={bar}
                                  className={`flex-1 rounded-full transition-colors ${
                                    joinAccountPwdStrength.score >= bar
                                      ? joinAccountPwdStrength.score <= 2
                                        ? 'bg-red-500'
                                        : joinAccountPwdStrength.score === 3
                                        ? 'bg-amber-500'
                                        : 'bg-emerald-500'
                                      : 'bg-slate-200'
                                  }`}
                                />
                              ))}
                            </div>
                            <p className="text-[11px] font-medium text-nature-muted">
                              Strength:{' '}
                              <span className="font-bold text-nature-text">{joinAccountPwdStrength.label}</span>
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="relative">
                        <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                          Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showJoinAccountConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="••••••••"
                            value={joinAccountForm.password_confirmation}
                            onChange={(e) =>
                              setJoinAccountForm((prev) => ({ ...prev, password_confirmation: e.target.value }))
                            }
                            className="w-full rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 pr-10 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowJoinAccountConfirmPassword(!showJoinAccountConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-nature-muted hover:text-nature-text"
                          >
                            {showJoinAccountConfirmPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>

                        {joinAccountForm.password_confirmation &&
                          joinAccountForm.password !== joinAccountForm.password_confirmation && (
                            <p className="mt-1 text-xs text-red-600 font-medium">Passwords do not match.</p>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Join Request Details */}
                  <div className="space-y-4 pt-3 border-t border-nature-muted/10">
                    <h3 className="text-base font-bold text-nature-text">
                      Request Details for {selectedJoinOrg.name}
                    </h3>

                    <div>
                      <Input
                        label="Designation / Role Title"
                        placeholder="e.g. Lead Event Coordinator, Student Head, Operations Admin"
                        value={joinAccountForm.designation}
                        onChange={(e) =>
                          setJoinAccountForm((prev) => ({ ...prev, designation: e.target.value }))
                        }
                        required
                      />
                    </div>

                    <div>
                      <Input
                        label="Department / Faculty (Optional)"
                        placeholder="e.g. Department of Computer Science, Cultural Committee"
                        value={joinAccountForm.department}
                        onChange={(e) =>
                          setJoinAccountForm((prev) => ({ ...prev, department: e.target.value }))
                        }
                      />
                    </div>

                    {joinOrgRoles.length > 0 && (
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                          Requested Role Preference
                        </label>
                        <select
                          value={joinAccountForm.role_id}
                          onChange={(e) =>
                            setJoinAccountForm((prev) => ({ ...prev, role_id: e.target.value }))
                          }
                          className="w-full rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10"
                        >
                          <option value="">-- Standard Organizer --</option>
                          {joinOrgRoles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.role_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
                        Request Notes for Administrator (Optional)
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Briefly describe your responsibilities or why you are joining this organization..."
                        value={joinAccountForm.notes}
                        onChange={(e) =>
                          setJoinAccountForm((prev) => ({ ...prev, notes: e.target.value }))
                        }
                        className="w-full rounded-lg bg-[#f3f4f6] border-0 px-4 py-2.5 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/10"
                      />
                    </div>
                  </div>

                  <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-nature-muted/10">
                    <Button type="button" variant="outline" onClick={() => setSelectedJoinOrg(null)}>
                      ← Back to Organization List
                    </Button>
                    <Button
                      type="submit"
                      loading={submittingJoinAccount}
                      disabled={!joinAccountForm.email_verified}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
                    >
                      <Send className="w-4 h-4" /> Create Account & Submit Join Request
                    </Button>
                  </div>
                </form>
              )}
            </Card>
          ) : (
            <div className="space-y-8">
              <div className="text-center max-w-2xl mx-auto">
                <div className="w-16 h-16 rounded-3xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-3xl font-bold text-nature-text">Join an Existing Organization</h1>
                <p className="text-nature-muted mt-2 text-sm">
                  Search for verified colleges, universities, corporate entities, or NGOs and request to join their team.
                </p>
              </div>

              {/* Search & Category Filter */}
              <div className="bg-bg/95 rounded-[32px] p-6 shadow-xl border border-nature-muted/10 space-y-4">
                <div className="relative">
                  <Search className="w-5 h-5 text-landing-primary absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search verified organizations by name, city, or type..."
                    value={joinSearchQuery}
                    onChange={(e) => setJoinSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white border border-nature-muted/20 text-nature-text placeholder:text-nature-muted/60 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-landing-primary/20 shadow-sm"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-xs font-bold text-nature-muted uppercase tracking-wider mr-1">Filter:</span>
                  {['All', 'College', 'University', 'Corporate', 'NGO', 'School', 'Government'].map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategoryFilter(cat)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                        selectedCategoryFilter === cat
                          ? 'bg-landing-primary text-white shadow-md'
                          : 'bg-white text-nature-muted hover:text-nature-text border border-nature-muted/20'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Organization Cards Grid */}
              {joinOrgsLoading ? (
                <div className="flex flex-col items-center justify-center py-16 text-nature-muted gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-landing-primary" />
                  <span className="text-sm font-medium">Searching verified organizations...</span>
                </div>
              ) : joinOrgsError ? (
                <Card variant="solid" className="rounded-[32px] border-none shadow-md bg-bg/95 p-12 text-center">
                  <Building2 className="w-12 h-12 text-rose-500/60 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-nature-text">Could not load organizations</h3>
                  <p className="text-nature-muted text-xs mt-1">{joinOrgsError}</p>
                  <Button
                    variant="outline"
                    className="mt-6"
                    onClick={() => {
                      setJoinOrgsLoading(true);
                      setJoinOrgsError(null);
                      organizerGovernanceApi
                        .searchOrganizations({ q: joinSearchQuery.trim(), per_page: 30 })
                        .then((res) => {
                          setJoinOrgs(res.data?.data?.data || res.data?.data || []);
                          setJoinOrgsError(null);
                        })
                        .catch((err) => {
                          const status = err?.response?.status;
                          setJoinOrgsError(
                            status === 401
                              ? 'Authentication required or session expired. Please sign in to view and join organizations.'
                              : err?.response?.data?.message || 'Failed to load organizations. Please try again later.'
                          );
                        })
                        .finally(() => setJoinOrgsLoading(false));
                    }}
                  >
                    Retry
                  </Button>
                </Card>
              ) : filteredJoinOrgs.length === 0 ? (
                <Card variant="solid" className="rounded-[32px] border-none shadow-md bg-bg/95 p-12 text-center">
                  <Building2 className="w-12 h-12 text-nature-muted/40 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-nature-text">No organizations found</h3>
                  <p className="text-nature-muted text-xs mt-1">Try adjusting your search query or category filter, or create a new organization.</p>
                  <Button variant="outline" className="mt-6" onClick={() => setMode('create')}>
                    Create New Organization Instead
                  </Button>
                </Card>
              ) : (
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {filteredJoinOrgs.map((org) => (
                    <div
                      key={org.id}
                      onClick={() => {
                        setSelectedJoinOrg(org);
                        setJoinForm({ designation: '', department: '', role_id: '', notes: '' });
                      }}
                      className="group text-left rounded-[28px] bg-white border border-nature-muted/15 shadow-md p-6 hover:shadow-xl hover:border-emerald-500/40 hover:-translate-y-1 transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-700 font-bold text-lg overflow-hidden shrink-0">
                            {resolveStorageUrl(org.logo_url) ? (
                              <img src={resolveStorageUrl(org.logo_url)!} alt={org.name} className="w-full h-full object-cover" />
                            ) : (
                              (org.name || 'O').charAt(0).toUpperCase()
                            )}
                          </div>
                          <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200">
                            {org.type || org.category || 'Verified'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <h3 className="text-base font-bold text-nature-text group-hover:text-emerald-700 transition-colors line-clamp-1">
                            {org.name}
                          </h3>
                          <BadgeCheck className="w-4 h-4 text-emerald-600 fill-emerald-50 shrink-0" />
                        </div>
                        {(org.city || org.country) && (
                          <p className="text-xs text-nature-muted mt-1.5 flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            {[org.city, org.country].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>

                      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-emerald-600 group-hover:text-emerald-700">
                        <span>Request to Join</span>
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-landing-bg via-white to-emerald-50/30 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-nature-text">Partner with EnthiosHub</h1>
            <p className="text-nature-muted font-medium mt-1">Register your organization to host world-class events.</p>
          </div>
          <button
            type="button"
            onClick={() => setMode('choose')}
            className="inline-flex items-center gap-2 text-sm font-bold text-landing-primary hover:text-landing-primaryHover transition-colors border-b-2 border-landing-primary/20 hover:border-landing-primary pb-0.5 w-fit"
          >
            ← Back to Options
          </button>
        </div>

        <Card variant="solid" className="overflow-hidden border-none shadow-[0_32px_120px_-20px_rgba(45,53,54,0.12)] rounded-[40px] bg-bg/95 backdrop-blur-sm">
          <CardHeader className="pt-10 pb-2 px-10">
            <CardTitle className="text-2xl font-bold text-nature-text">Create Organization Account</CardTitle>
            <p className="text-base text-nature-muted mt-2">Submit details for admin's approval. Fields marked with * are required.</p>
          </CardHeader>

          <CardContent className="p-10">
            <RegistrationStepIndicator step={step} totalSteps={totalSteps} />

            <form onSubmit={onSubmit} className="space-y-8 mt-4">
              {step === 1 && (
                <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-md mx-auto py-10">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-landing-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-10 h-10 text-landing-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-nature-text">Verify Organization Email</h3>
                    <p className="text-nature-muted mt-2">We&apos;ll send a code to verify your organization.</p>
                  </div>

                  <div className="space-y-6">
                    <div className="relative">
                      <Input
                        label="Organization Email"
                        type="email"
                        value={form.organization_email}
                        onChange={(e) => {
                          update('organization_email', e.target.value);
                          setOrgOtpSent(false);
                          setOtpError(null);
                          setOtpRetryable(false);
                          update('otp', '');
                          update('email_verified', false);
                        }}
                        disabled={form.email_verified || otpLoading}
                        required
                      />
                      {!form.email_verified && (
                        <button
                          type="button"
                          onClick={() => handleSendOtp('organization')}
                          disabled={otpLoading || !form.organization_email || otpResendWaitSeconds > 0}
                          className="absolute right-2 bottom-1.5 px-3 py-1 bg-landing-primary/10 text-landing-primary text-xs font-bold rounded-lg hover:bg-landing-primary/20 transition-colors disabled:opacity-50"
                        >
                          {otpLoading ? 'Sending...' : otpResendWaitSeconds > 0 ? `Wait ${otpResendWaitSeconds}s` : otpRetryable && otpError ? 'Try again' : 'Get Code'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-nature-muted -mt-4">
                      Note: Organization Email and Admin Email can be the same or different.
                    </p>

                    {otpError && !form.email_verified && (
                      <div className="flex items-start justify-between gap-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                        <span>{otpError}</span>
                        {otpRetryable && (
                          <button
                            type="button"
                            onClick={() => handleSendOtp('organization')}
                            disabled={otpLoading}
                            className="shrink-0 font-semibold underline underline-offset-2 disabled:opacity-50"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    )}
                    {orgOtpSent && form.organization_email && !form.email_verified && (
                      <div className="animate-in fade-in zoom-in-95 duration-300 space-y-3">
                        <Input
                          label="Enter 6-digit Code"
                          placeholder="000000"
                          value={form.otp}
                          onChange={(e) => {
                            setOtpError(null);
                            update('otp', e.target.value.replace(/\D/g, '').slice(0, 6));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!otpLoading && !form.email_verified && !verifyingOrgOtpRef.current && form.otp.trim().length === 6) {
                                handleVerifyOtp('organization');
                              }
                            }
                          }}
                          inputMode="numeric"
                          required
                        />
                        <Button
                          type="button"
                          variant="primary"
                          fullWidth
                          className="mt-6"
                          onClick={() => handleVerifyOtp('organization')}
                          loading={otpLoading}
                          disabled={form.otp.trim().length !== 6 || form.email_verified || verifyingOrgOtpRef.current}
                        >
                          Verify & Continue
                        </Button>
                      </div>
                    )}

                    {form.email_verified && (
                      <div className="flex flex-col items-center p-6 bg-emerald-50 rounded-2xl border border-emerald-100">
                        <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                        <span className="text-emerald-700 font-bold">Email Verified</span>
                        <Button type="button" variant="ghost" className="mt-4 text-emerald-600 hover:bg-emerald-100" onClick={() => setStep(2)}>
                          Proceed to Details <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {step === 2 && (
                <section className="animate-in fade-in slide-in-from-right-4 duration-500">
                  <div className="flex items-center gap-2 mb-8 border-b border-landing-border pb-2">
                    <div className="w-1.5 h-6 bg-landing-primary rounded-full" />
                    <h3 className="text-xl font-bold text-landing-text">Organization Identity</h3>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <Input label="Organization Name" value={form.name} onChange={(e) => update('name', e.target.value)} required />
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                        Organization Type<span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <select
                        value={form.type}
                        onChange={(e) => update('type', e.target.value as OrgType)}
                        className="input-base input-focus w-full"
                        required
                      >
                        {orgTypeOptions.map((item) => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    </div>
                    <Input label="Verified Email" type="email" value={form.organization_email} disabled required />
                    <RegistrationPhoneField
                      label="Phone Number"
                      value={form.phone_number}
                      dialCode={form.country_code}
                      onChange={(digits) => update('phone_number', digits)}
                      required
                    />
                    <Input label="Website" value={form.website} onChange={(e) => update('website', e.target.value)} required />
                    <Input label="Year Established" type="number" value={form.year_established} onChange={(e) => update('year_established', e.target.value)} required />
                  </div>
                </section>
              )}

              {step === 3 && (
                <section className="animate-in fade-in slide-in-from-right-4 duration-500">
                  <div className="flex items-center gap-2 mb-8 border-b border-landing-border pb-2">
                    <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                    <h3 className="text-xl font-bold text-landing-text">Location & Address</h3>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <LocationSelector
                        country={form.country}
                        state={form.state}
                        city={form.city}
                        onChange={(patch) => {
                          setForm((prev) => ({ ...prev, ...patch }));
                        }}
                        required
                      />
                    </div>
                    <Input label="Postal Code" value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)} required />
                    <div className="md:col-span-2">
                      <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                        Address<span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <textarea
                        value={form.address}
                        onChange={(e) => update('address', e.target.value)}
                        className="input-base input-focus min-h-24 w-full"
                        required
                      />
                    </div>
                  </div>
                </section>
              )}

              {step === 4 && (
                <section className="animate-in fade-in slide-in-from-right-4 duration-500">
                  <div className="flex items-center gap-2 mb-8 border-b border-landing-border pb-2">
                    <div className="w-1.5 h-6 bg-nature-muted rounded-full" />
                    <h3 className="text-xl font-bold text-landing-text">Verification & Socials</h3>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <Input label="Registration Number" value={form.registration_number} onChange={(e) => update('registration_number', e.target.value)} required />
                    <Input label="Primary Contact Person" value={form.contact_person} onChange={(e) => update('contact_person', e.target.value)} required />
                    <div>
                      <label className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                        Verification Document<span className="text-red-500 ml-0.5">*</span>
                        <span className="relative group cursor-pointer">
                          <Info className="w-3.5 h-3.5 text-nature-muted hover:text-nature-text" />
                          <span className="absolute left-5 top-0 z-10 hidden group-hover:block w-72 rounded-lg bg-dark p-2.5 text-xs text-text-inverse shadow-xl">
                            Please upload a letter from the Head of the Institution (HOI) or another authorized person from the institution. The letter must be issued on the institution’s official letterhead and should clearly mention the required details. It must also include the official institutional stamp and the signature of the HOI or authorized person.
                          </span>
                        </span>
                      </label>
                      <p className="mb-2 text-xs text-nature-muted">PDF or image, max 2 MB.</p>
                      
                      <input
                        ref={docInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          const msg = validateVerificationDocument(file, true);
                          if (msg) {
                            toast.error(msg);
                            e.target.value = '';
                            setFiles((prev) => ({ ...prev, verification_document: null }));
                            return;
                          }
                          setFiles((prev) => ({ ...prev, verification_document: file }));
                        }}
                        className="hidden"
                      />

                      {files.verification_document ? (
                        <div className="p-3.5 rounded-2xl bg-white border border-nature-muted/20 shadow-sm flex items-center justify-between gap-3 transition-all hover:border-landing-primary/40">
                          <div className="flex items-center gap-3 min-w-0">
                            {verificationDocPreviewUrl ? (
                              <img
                                src={verificationDocPreviewUrl}
                                alt="Verification document preview"
                                className="w-12 h-12 rounded-xl object-cover border border-landing-border shrink-0"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center shrink-0">
                                <FileText className="w-6 h-6" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-nature-text truncate">{files.verification_document.name}</p>
                              <p className="text-[11px] text-nature-muted mt-0.5">
                                {(files.verification_document.size / 1024).toFixed(1)} KB • {files.verification_document.type.includes('pdf') || files.verification_document.name.endsWith('.pdf') ? 'PDF Document' : 'Image'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => docInputRef.current?.click()}
                              className="px-2.5 py-1 text-xs font-semibold text-landing-primary hover:bg-landing-primary/10 rounded-lg transition-colors cursor-pointer"
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (docInputRef.current) docInputRef.current.value = '';
                                setFiles((prev) => ({ ...prev, verification_document: null }));
                              }}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer"
                              title="Remove file"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => docInputRef.current?.click()}
                          className="w-full py-4 px-4 rounded-2xl border-2 border-dashed border-nature-muted/30 hover:border-landing-primary bg-bg/50 hover:bg-white text-center transition-all group cursor-pointer flex flex-col items-center justify-center"
                        >
                          <Upload className="w-5 h-5 text-nature-muted group-hover:text-landing-primary transition-colors mb-1" />
                          <span className="text-xs font-bold text-nature-text group-hover:text-landing-primary">Click to select Verification Document</span>
                          <span className="text-[11px] text-nature-muted mt-0.5">PDF, JPG, or PNG up to 2 MB</span>
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                        Organization Logo<span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <p className="mb-2 text-xs text-nature-muted">JPG or PNG, max 2 MB.</p>
                      
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          const msg = validateOrgLogo(file, true);
                          if (msg) {
                            toast.error(msg);
                            e.target.value = '';
                            setFiles((prev) => ({ ...prev, logo: null }));
                            return;
                          }
                          setFiles((prev) => ({ ...prev, logo: file }));
                        }}
                        className="hidden"
                      />

                      {files.logo ? (
                        <div className="p-3.5 rounded-2xl bg-white border border-nature-muted/20 shadow-sm flex items-center justify-between gap-3 transition-all hover:border-landing-primary/40">
                          <div className="flex items-center gap-3 min-w-0">
                            {logoPreviewUrl ? (
                              <img
                                src={logoPreviewUrl}
                                alt="Organization logo preview"
                                className="w-12 h-12 rounded-xl object-contain border border-landing-border bg-slate-50 p-1 shrink-0"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center shrink-0">
                                <Building2 className="w-6 h-6" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-nature-text truncate">{files.logo.name}</p>
                              <p className="text-[11px] text-nature-muted mt-0.5">
                                {(files.logo.size / 1024).toFixed(1)} KB • Image
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => logoInputRef.current?.click()}
                              className="px-2.5 py-1 text-xs font-semibold text-landing-primary hover:bg-landing-primary/10 rounded-lg transition-colors cursor-pointer"
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (logoInputRef.current) logoInputRef.current.value = '';
                                setFiles((prev) => ({ ...prev, logo: null }));
                              }}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer"
                              title="Remove file"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => logoInputRef.current?.click()}
                          className="w-full py-4 px-4 rounded-2xl border-2 border-dashed border-nature-muted/30 hover:border-landing-primary bg-bg/50 hover:bg-white text-center transition-all group cursor-pointer flex flex-col items-center justify-center"
                        >
                          <Upload className="w-5 h-5 text-nature-muted group-hover:text-landing-primary transition-colors mb-1" />
                          <span className="text-xs font-bold text-nature-text group-hover:text-landing-primary">Click to select Organization Logo</span>
                          <span className="text-[11px] text-nature-muted mt-0.5">PNG, JPG, or WEBP up to 2 MB</span>
                        </button>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {step === 5 && (
                <section className="animate-in fade-in slide-in-from-right-4 duration-500">
                  <div className="flex items-center gap-2 mb-8 border-b border-landing-border pb-2">
                    <div className="w-1.5 h-6 bg-moss rounded-full" />
                    <h3 className="text-xl font-bold text-landing-text">Admin Account</h3>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <Input label="Admin Full Name" value={form.admin_full_name} onChange={(e) => update('admin_full_name', e.target.value)} required />
                    <div className="relative">
                      <Input
                        label="Admin Email"
                        type="email"
                        value={form.admin_email}
                        onChange={(e) => {
                          update('admin_email', e.target.value);
                          setAdminOtpSent(false);
                          setOtpError(null);
                          setOtpRetryable(false);
                          update('admin_otp', '');
                          update('admin_email_verified', false);
                        }}
                        disabled={form.admin_email_verified && adminEmailNeedsVerification}
                        required
                      />
                      {adminEmailNeedsVerification && !form.admin_email_verified && form.admin_email && (
                        <button
                          type="button"
                          onClick={() => handleSendOtp('admin')}
                          disabled={adminOtpLoading || adminOtpResendWaitSeconds > 0}
                          className="absolute right-2 bottom-1.5 px-3 py-1 bg-landing-primary/10 text-landing-primary text-xs font-bold rounded-lg"
                        >
                          {adminOtpLoading ? 'Sending...' : adminOtpResendWaitSeconds > 0 ? `Wait ${adminOtpResendWaitSeconds}s` : otpRetryable && otpError ? 'Try again' : 'Get Code'}
                        </button>
                      )}
                    </div>
                    {adminEmailNeedsVerification && !form.admin_email_verified && form.admin_email && (otpError || adminOtpSent) && (
                      <div className="md:col-span-2 space-y-2">
                        {otpError && (
                          <div className="flex items-start justify-between gap-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                            <span>{otpError}</span>
                            {otpRetryable && (
                              <button type="button" onClick={() => handleSendOtp('admin')} disabled={adminOtpLoading} className="shrink-0 font-semibold underline underline-offset-2 disabled:opacity-50">
                                Retry
                              </button>
                            )}
                          </div>
                        )}
                        {adminOtpSent && (
                          <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                            <Input
                              label="Admin Email OTP"
                              value={form.admin_otp}
                              onChange={(e) => { setOtpError(null); update('admin_otp', e.target.value.replace(/\D/g, '').slice(0, 6)); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (!adminOtpLoading && !form.admin_email_verified && !verifyingAdminOtpRef.current && form.admin_otp.trim().length === 6) {
                                    handleVerifyOtp('admin');
                                  }
                                }
                              }}
                              inputMode="numeric"
                              required
                            />
                            <Button type="button" onClick={() => handleVerifyOtp('admin')} loading={adminOtpLoading} disabled={form.admin_otp.trim().length !== 6 || form.admin_email_verified || verifyingAdminOtpRef.current} className="self-end">
                              Verify Admin Email
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    <RegistrationPhoneField
                      label="Admin Phone"
                      value={form.admin_phone}
                      dialCode={form.admin_phone_country_code}
                      onChange={(digits) => update('admin_phone', digits)}
                      required
                    />
                    <Input label="Admin Designation" value={form.admin_designation} onChange={(e) => update('admin_designation', e.target.value)} required />
                    <Input
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.admin_password}
                      onChange={(e) => update('admin_password', e.target.value)}
                      onKeyUp={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
                      required
                      rightIcon={
                        <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label="Toggle password">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      }
                    />
                    <Input
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.admin_password_confirmation}
                      onChange={(e) => update('admin_password_confirmation', e.target.value)}
                      required
                      rightIcon={
                        <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} aria-label="Toggle confirm password">
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      }
                    />
                    <div className="md:col-span-2">
                      <p className="text-xs font-semibold text-nature-muted mb-2">
                        Password strength: <span className="text-nature-text">{pwdStrength.label}</span>
                        {capsLockOn && <span className="ml-2 text-amber-600">Caps Lock is on</span>}
                      </p>
                      <ul className="grid gap-1 sm:grid-cols-2 text-xs">
                        {pwdStrength.checks.map((check) => (
                          <li key={check.label} className={check.ok ? 'text-emerald-600' : 'text-nature-muted'}>
                            {check.ok ? '✓' : '○'} {check.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>
              )}

              <div className="flex items-center justify-between pt-8 border-t border-landing-border">
                {step > 1 ? (
                  <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)} disabled={loading || otpLoading}>
                    Back
                  </Button>
                ) : (
                  <span />
                )}

                {step < totalSteps ? (
                  step !== 1 && (
                    <Button type="button" onClick={handleContinue} disabled={loading || otpLoading}>
                      Continue
                    </Button>
                  )
                ) : (
                  <Button type="submit" disabled={!canSubmit || loading} loading={loading}>
                    Complete Registration
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function OrganizationRegistrationPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-landing-bg via-white to-emerald-50/30">
          <Loader2 className="h-10 w-10 animate-spin text-landing-primary" />
        </div>
      }
    >
      <OrganizationRegistrationContent />
    </Suspense>
  );
}
